import React, { useState } from 'react';
import {
	Typography,
	makeStyles,
	Paper,
	Grid,
	CircularProgress,
	Backdrop,
} from '@material-ui/core';
import CustomButton from './CustomButton';
import UtilInputs from './UtilInputs';
import './styles/_fullTable.scss';
import { bitacoraApi } from 'src/api/modules/bitacora';
import ExportCSV from 'src/common/components/ExportCSV';
import { jsPDF } from 'jspdf';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { InboxSharp } from '@material-ui/icons';
import ExportXLSX from './ExportXLSX';
import CreateExcel from './CreateExcel';

const useStyles = makeStyles({
	root: {
		margin: 0,
		width: '100%',
		'& .row': {
			marginTop: 10,
		},
		'& .btn.btn-secondary': {
			marginRight: 10,
			color: '#3699FF !important',
			background: '#ffffff !important',
			border: '1px solid #E4E6EF !important',
			'&:hover': {
				background: '#ffffff !important',
				border: '1px solid #E4E6EF !important',
				color: '#3699FF !important',
			},
		},
		'& .btn.btn-secondary.dropdown-toggle:after': {
			color: '#3699FF  !important',
		},
	},
	title: {
		fontSize: 21,
		marginBottom: 15,
	},
	columnButtons: {
		display: 'flex',
		justifyContent: 'flex-end',
		height: '53px',
	},
});

const FullTable = ({
	idTable,
	title = '',
	keyField = 'id',
	hideFooter = false,
	columns = [],
	items = [],
	itemsOthers = [],
	disabledCSV,
	disabledExcel,
	page = 0,
	onPageChange,
	limit = 10,
	count = 1,
	paginateOptions = [10, 20, 50],
	onChangeLimit = () => {},
	buttons = [],
	filter = null,
	filterForm = [],
	buttonsfilterForm = [],
	onFilterChange = () => {},
	onConfirmFilter = () => {},
	filtersWidth = 200,
	onSelectRow = () => {},
	onSelectAllRows = () => {},
	handlePagination = () => {},
	nonSelectableRows = [],
	has_checkbox = false,
	CVSFileName = '',
	ExcelFileName = '',
	XLSXFileName = '',
	ExcelPersonalized = '',
	PDFFileName = '',
	expand,
	dataRow,
	showPagination = false,
	sizePerPageTable = 5,
	totalPages = 0,
	currentPage = 1,
	setCurrentPage,
	useBackendPagination = true,
	isLoading = false,
	createExcel = '',
	otherItems = [],
	disabledPdf = false,
}) => {
	const classes = useStyles();
	const [loader, setLoader] = useState(false);

	const generatorPDF = async () => {
		setLoader(true);

		const dataSource = otherItems.length > 0 ? otherItems : items;
		const headers = columns.map((col) => col.text);
		const fields = columns.map((col) => col.dataField);

	
		const pdf = new jsPDF('l', 'mm', 'a2');

		const startX = 10;
		const startY = 20;

		const pageWidth = pdf.internal.pageSize.getWidth() - startX * 2;

		const fontSize = 8;
		const lineHeight = 3;
		const cellPadding = 2;

		
		const defaultColWidth = 50;
		const maxColWidth = 120; 

		const colWidths = headers.map((header, i) => {
			const field = fields[i];
			let maxTextLength = header.length;

			dataSource.forEach((row) => {
				const value = (row[field] ?? '').toString();
				if (value.length > maxTextLength) {
					maxTextLength = value.length;
				}
			});

			let width = defaultColWidth;
			if (maxTextLength > 20 && maxTextLength <= 50) width = 70;
			if (maxTextLength > 50 && maxTextLength <= 100) width = 90;
			if (maxTextLength > 100) width = maxColWidth;

			return width;
		});

		const totalColsWidth = colWidths.reduce((a, b) => a + b, 0);
		if (totalColsWidth > pageWidth) {
			const scale = pageWidth / totalColsWidth;
			for (let i = 0; i < colWidths.length; i++) {
				colWidths[i] = colWidths[i] * scale;
			}
		}

		pdf.setFont('helvetica', 'bold');
		pdf.setFontSize(fontSize);

		const headerHeight = lineHeight + 6;
		let x = startX;

		headers.forEach((header, i) => {
			const cellWidth = colWidths[i];
			pdf.rect(x, startY, cellWidth, headerHeight);

			const headerLines = pdf.splitTextToSize(
				header.toUpperCase(),
				cellWidth - cellPadding * 2
			);
			const totalHeaderHeight = headerLines.length * lineHeight;
			const yOffset = (headerHeight - totalHeaderHeight) / 2;

			headerLines.forEach((line, index) => {
				const yPos = startY + yOffset + (index + 1) * lineHeight - 1;
				pdf.text(line, x + cellPadding, yPos);
			});

			x += cellWidth;
		});

		let y = startY + headerHeight;

		pdf.setFont('helvetica', 'normal');
		pdf.setFontSize(fontSize);

		dataSource.forEach((row) => {
			const cellTexts = fields.map((field, i) => {
				const value = row[field] ?? '';
				return pdf.splitTextToSize(
					value.toString(),
					colWidths[i] - cellPadding * 2
				);
			});

			const maxLines = Math.max(...cellTexts.map((lines) => lines.length));
			const rowHeight = maxLines * lineHeight + 2;
			x = startX;

			cellTexts.forEach((lines, i) => {
				const cellWidth = colWidths[i];
				pdf.rect(x, y, cellWidth, rowHeight);

				const totalTextHeight = lines.length * lineHeight;
				const yOffset = (rowHeight - totalTextHeight) / 2;

				lines.forEach((line, index) => {
					const yPos = y + yOffset + (index + 1) * lineHeight - 1;
					pdf.text(line, x + cellPadding, yPos);
				});

				x += cellWidth;
			});

			y += rowHeight;

			if (y > pdf.internal.pageSize.getHeight() - startY) {
				pdf.addPage();
				y = startY;
			}
		});

		pdf.save(`${PDFFileName}.pdf`);
		setLoader(false);

		bitacoraApi.post({
			accion: 'Descargo PDF ' + `${PDFFileName}.pdf`,
		});
	};

	const expandRow = {
		renderer: (row) => dataRow(row),
		showExpandColumn: true,
	};

	const loading = () => {
		setLoader(true);
		generatorPDF();
	};

	const noDataMessage = () => {
		return isLoading ? (
			<div
				style={{
					padding: '20px',
					color: '#6c757d',
					fontWeight: 'bold',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					gap: '10px',
					flexDirection: 'column',
					width: '100%',
					fontSize: '18px',
					height: '200px',
				}}
			>
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden"></span>
				</div>
				Cargando datos...
			</div>
		) : (
			<div
				style={{
					padding: '20px',
					color: '#6c757d',
					fontWeight: 'bold',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					gap: '10px',
					flexDirection: 'column',
					width: '100%',
					fontSize: '18px',
					height: '200px',
				}}
			>
				<InboxSharp style={{ fontSize: '40px', color: '#6c757d' }} />
				Sin datos
			</div>
		);
	};

	return (
		<Paper elevation={0} className="w-100 bg-white">
			{(title ||
				CVSFileName ||
				ExcelFileName ||
				ExcelPersonalized ||
				PDFFileName ||
				buttons.length !== 0) && (
				<Grid container className={classes.root} spacing={2}>
					<Grid item md={7}>
						<Typography variant="h6" className={classes.title}>
							{title}
						</Typography>
					</Grid>
					<Grid item className={classes.columnButtons} md={5}>
						{CVSFileName && !disabledCSV && (
							<ExportCSV
								data={items}
								columns={columns}
								fileName={CVSFileName}
								className="mr-3"
							/>
						)}
						{ExcelFileName && !disabledExcel && (
							<ExportCSV
								data={items}
								columns={columns}
								fileName={ExcelFileName}
								label="Descargar Excel"
							/>
						)}
						{XLSXFileName && !disabledExcel && (
							<ExportXLSX
								data={items}
								columns={columns}
								fileName={XLSXFileName}
								label="Descargar Excel"
							/>
						)}
						{createExcel && !disabledExcel && (
							<CreateExcel
								data={otherItems.length === 0 ? items : otherItems}
								columns={columns}
								fileName={createExcel}
								label="Descargar Excel"
							/>
						)}
						{ExcelPersonalized && (
							<ExportCSV
								data={itemsOthers}
								columns={columns}
								fileName={ExcelPersonalized}
								label="Descargar Excel"
							/>
						)}
						{buttons.map((button) => (
							<CustomButton
								key={button.title}
								mainText={button.title}
								color={button.color}
								onClick={button.onClick}
								outlined={button.outlined}
								disabled={button.disabled}
								className={button.className}
								startIcon={button.startIcon}
								endIcon={button.endIcon}
								style={button.style}
							/>
						))}
						{PDFFileName && !disabledPdf && (
							<CustomButton
								className="PDF ml-3"
								color="info"
								mainText="Descargar PDF"
								onClick={() => {
									loading();
								}}
							/>
						)}
						{
							<Backdrop open={loader} style={{ zIndex: 1 }}>
								<CircularProgress color="secondary" />
							</Backdrop>
						}
					</Grid>
				</Grid>
			)}
			{filterForm.length > 0 && (
				<div className="transparent-container">
					<UtilInputs
						formValues={filter}
						onValuesChange={onFilterChange}
						inputValues={filterForm}
					/>
					{buttonsfilterForm.map((button) => (
						<CustomButton
							mainText={button.title}
							color="light-warning"
							onClick={onConfirmFilter}
							className="mb-1 mr-3"
						/>
					))}
				</div>
			)}

			<Grid container className={classes.root} spacing={2}>
				<Grid item md={12} style={{ width: '100%' }}>
					<BootstrapTable
						id={idTable}
						keyField={keyField}
						remote={
							useBackendPagination
								? { pagination: true, page: currentPage }
								: false
						}
						bordered={false}
						wrapperClasses="table-responsive"
						classes="table table-head-custom table-vertical-center border border-top"
						data={items}
						columns={columns}
						expandRow={expand === true ? expandRow : undefined}
						selectRow={{
							mode: 'checkbox',
							onSelect: onSelectRow,
							onSelectAll: onSelectAllRows,
							nonSelectable: nonSelectableRows,
							hideSelectAll: !has_checkbox,
							hideSelectColumn: !has_checkbox,
						}}
						onTableChange={
							useBackendPagination
								? (a, b) => handlePagination(b.sizePerPage)
								: undefined
						}
						pagination={
							showPagination
								? paginationFactory({
										sizePerPage: sizePerPageTable,
										sizePerPageList: [
											{ text: '5', value: 5 },
											{ text: '10', value: 10 },
											{ text: '20', value: 20 },
											{ text: '50', value: 50 },
											{ text: '100', value: 100 },
											{ text: '200', value: 200 },
											{ text: '500', value: 500 },
											{ text: 'Todos', value: totalPages },
										],
										onSizePerPageChange: useBackendPagination
											? (sizePerPage, page) =>
													handlePagination(page)
											: undefined,
										showTotal: true,
										page: currentPage,
										totalSize: useBackendPagination
											? totalPages
											: items.length,
										paginationTotalRenderer: (from, to, size) =>
											`${from} - ${to} de ${size} registros`,
										onPageChange: (page) => {
											if (useBackendPagination) {
												setCurrentPage(page);
											}
										},
								  })
								: false
						}
						noDataIndication={noDataMessage}
					/>
				</Grid>
			</Grid>
		</Paper>
	);
};

export default FullTable;
